import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { PwaService } from './pwa.service';

type ActiveTab = 'install' | 'engine' | 'shortcuts';

@Component({
  selector: 'app-pwa-install-prompt',
  imports: [CommonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isOpen()) {
      <div
        class="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-5 bg-black/65 dark:bg-black/85 backdrop-blur-2xl animate-ios-fade select-none overflow-y-auto"
        (click)="onBackdropClick($event)"
      >
        <!-- Liquid Glass Modal Container -->
        <div
          class="relative w-full max-w-lg rounded-[36px] sm:rounded-[42px] bg-white/90 dark:bg-[#121217]/90 backdrop-blur-3xl border border-white/60 dark:border-white/10 shadow-[0_25px_90px_-15px_rgba(0,0,0,0.6)] overflow-hidden p-5 sm:p-8 flex flex-col items-center text-center space-y-5 animate-ios-slide my-auto"
          (click)="$event.stopPropagation()"
        >
          <!-- Specular Shimmer Top Line -->
          <div class="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-ios-blue via-ios-purple to-pink-500 opacity-90"></div>

          <!-- Ambient Glow Accents -->
          <div class="absolute -top-24 -left-24 w-60 h-60 bg-ios-blue/20 rounded-full blur-[90px] pointer-events-none"></div>
          <div class="absolute -bottom-24 -right-24 w-60 h-60 bg-purple-500/20 rounded-full blur-[90px] pointer-events-none"></div>

          <!-- Top Bar: Close Button -->
          <div class="w-full flex items-center justify-between relative z-10 pt-1">
            <div class="flex items-center gap-2">
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue text-[11px] font-extrabold tracking-wider uppercase border border-ios-blue/25">
                <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                {{ pwaService.platformName() }}
              </span>
              @if (pwaService.isInstalled()) {
                <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold border border-emerald-500/30">
                  <mat-icon class="!text-[14px]">check_circle</mat-icon> Installed
                </span>
              }
            </div>

            <button
              type="button"
              (click)="onClose()"
              class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/10 text-slate-500 hover:text-slate-900 dark:hover:text-white flex items-center justify-center transition-all cursor-pointer active:scale-90"
              title="Close"
            >
              <mat-icon class="!text-[18px]">close</mat-icon>
            </button>
          </div>

          <!-- Circular Kicgle 3D-Style Icon -->
          <div class="relative z-10 flex items-center justify-center pt-1">
            <div
              class="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-1 bg-gradient-to-tr from-ios-blue via-ios-purple to-pink-500 shadow-[0_12px_40px_rgba(0,122,255,0.4)] flex items-center justify-center relative group"
            >
              <div
                class="w-full h-full rounded-full bg-white dark:bg-[#181822] flex items-center justify-center p-3.5 overflow-hidden border border-white/60 dark:border-white/15 shadow-inner"
              >
                <img
                  src="kicgle.svg"
                  alt="Kicgle Logo"
                  class="w-full h-full object-contain filter drop-shadow group-hover:scale-110 transition-transform duration-500"
                  referrerpolicy="no-referrer"
                />
              </div>

              <!-- Target Platform OS Badge -->
              <div
                class="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-ios-blue text-white flex items-center justify-center shadow-lg border-2 border-white dark:border-[#121217]"
                [title]="pwaService.platformName() + ' detected'"
              >
                <mat-icon class="!text-[18px]">{{ platformIcon() }}</mat-icon>
              </div>
            </div>
          </div>

          <!-- Title & Subtitle -->
          <div class="relative z-10 space-y-1.5">
            <h2 class="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white">
              {{ pwaService.isInstalled() ? 'Kicgle App Engine' : 'Install Kicgle' }}
            </h2>
            <p class="text-xs sm:text-sm font-medium text-slate-600 dark:text-zinc-300 leading-relaxed max-w-sm mx-auto">
              {{ pwaService.isInstalled() 
                ? 'Your next-gen PWA workspace is active with offline sync and native hardware capabilities.' 
                : 'Experience zero latency, offline persistence, standalone window framing, and instant launcher access.' }}
            </p>
          </div>

          <!-- Futuristic Segmented Control / Navigation Tabs -->
          <div class="relative z-10 w-full p-1 rounded-2xl bg-black/5 dark:bg-white/[0.06] border border-black/5 dark:border-white/10 flex items-center gap-1 text-xs font-bold text-slate-600 dark:text-zinc-400">
            <button
              type="button"
              (click)="activeTab.set('install')"
              class="flex-1 py-1.5 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5"
              [class.bg-white]="activeTab() === 'install'"
              [class.dark:bg-[#1c1c24]]="activeTab() === 'install'"
              [class.text-ios-blue]="activeTab() === 'install'"
              [class.shadow-sm]="activeTab() === 'install'"
            >
              <mat-icon class="!text-[16px]">install_mobile</mat-icon>
              <span>{{ pwaService.isInstalled() ? 'App Status' : 'Install Guide' }}</span>
            </button>

            <button
              type="button"
              (click)="activeTab.set('engine')"
              class="flex-1 py-1.5 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5"
              [class.bg-white]="activeTab() === 'engine'"
              [class.dark:bg-[#1c1c24]]="activeTab() === 'engine'"
              [class.text-ios-blue]="activeTab() === 'engine'"
              [class.shadow-sm]="activeTab() === 'engine'"
            >
              <mat-icon class="!text-[16px]">memory</mat-icon>
              <span>PWA Engine</span>
            </button>

            <button
              type="button"
              (click)="activeTab.set('shortcuts')"
              class="flex-1 py-1.5 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5"
              [class.bg-white]="activeTab() === 'shortcuts'"
              [class.dark:bg-[#1c1c24]]="activeTab() === 'shortcuts'"
              [class.text-ios-blue]="activeTab() === 'shortcuts'"
              [class.shadow-sm]="activeTab() === 'shortcuts'"
            >
              <mat-icon class="!text-[16px]">bolt</mat-icon>
              <span>Shortcuts</span>
            </button>
          </div>

          <!-- TAB 1: INSTALL / STATUS -->
          @if (activeTab() === 'install') {
            <div class="relative z-10 w-full space-y-3.5 text-left animate-ios-fade">
              <!-- If native 1-click install is available -->
              @if (isNativePromptAvailable()) {
                <div class="p-3.5 rounded-2xl bg-ios-blue/10 dark:bg-ios-blue/15 border border-ios-blue/30 flex items-center justify-between gap-3">
                  <div class="flex items-center gap-2.5 min-w-0">
                    <div class="w-8 h-8 rounded-full bg-ios-blue text-white flex items-center justify-center shrink-0 shadow-md">
                      <mat-icon class="!text-[18px]">verified</mat-icon>
                    </div>
                    <div>
                      <p class="text-xs font-black text-slate-900 dark:text-white">1-Click Install Ready</p>
                      <p class="text-[11px] text-slate-600 dark:text-zinc-400">Native browser prompt is primed for immediate installation.</p>
                    </div>
                  </div>
                </div>
              } @else if (pwaService.detectedOS() === 'ios') {
                <!-- iOS Safari Step Guide -->
                <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/[0.04] border border-black/5 dark:border-white/10 space-y-2.5">
                  <div class="flex items-center gap-2 text-xs font-black text-slate-900 dark:text-white">
                    <mat-icon class="text-ios-blue !text-[18px]">apple</mat-icon>
                    <span>Safari on iPhone & iPad:</span>
                  </div>
                  <div class="grid grid-cols-3 gap-2 pt-1">
                    <div class="p-2 rounded-xl bg-white/60 dark:bg-black/40 border border-black/5 dark:border-white/10 flex flex-col items-center text-center space-y-1">
                      <div class="w-6 h-6 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                        <mat-icon class="!text-[14px]">ios_share</mat-icon>
                      </div>
                      <span class="text-[10px] font-bold text-slate-700 dark:text-zinc-300">1. Tap Share</span>
                    </div>
                    <div class="p-2 rounded-xl bg-white/60 dark:bg-black/40 border border-black/5 dark:border-white/10 flex flex-col items-center text-center space-y-1">
                      <div class="w-6 h-6 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                        <mat-icon class="!text-[14px]">add_box</mat-icon>
                      </div>
                      <span class="text-[10px] font-bold text-slate-700 dark:text-zinc-300">2. Add to Home</span>
                    </div>
                    <div class="p-2 rounded-xl bg-white/60 dark:bg-black/40 border border-black/5 dark:border-white/10 flex flex-col items-center text-center space-y-1">
                      <div class="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                        <mat-icon class="!text-[14px]">done</mat-icon>
                      </div>
                      <span class="text-[10px] font-bold text-slate-700 dark:text-zinc-300">3. Tap Add</span>
                    </div>
                  </div>
                </div>
              } @else if (pwaService.detectedOS() === 'windows' || pwaService.detectedOS() === 'mac' || pwaService.detectedOS() === 'linux') {
                <!-- Desktop Browser Guide -->
                <div class="p-3.5 rounded-2xl bg-black/5 dark:bg-white/[0.04] border border-black/5 dark:border-white/10 space-y-1.5">
                  <div class="flex items-center gap-2 text-xs font-black text-slate-900 dark:text-white">
                    <mat-icon class="text-ios-blue !text-[18px]">desktop_windows</mat-icon>
                    <span>Desktop Omnibox / Browser Menu:</span>
                  </div>
                  <p class="text-[11px] text-slate-600 dark:text-zinc-400 leading-relaxed">
                    Look for the <strong class="text-ios-blue font-bold">Install App</strong> icon in the right side of the address bar, or click browser menu (⋮) &rarr; <strong class="text-ios-blue font-bold">Install Kicgle</strong>.
                  </p>
                </div>
              }

              <!-- 4 Feature Highlights Grid -->
              <div class="grid grid-cols-2 gap-2 text-left">
                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-start gap-2">
                  <div class="w-7 h-7 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[16px]">bolt</mat-icon>
                  </div>
                  <div>
                    <p class="text-[11px] font-black text-slate-900 dark:text-white">0ms Cold Start</p>
                    <p class="text-[9px] text-slate-500 dark:text-zinc-400">Prefetched service worker</p>
                  </div>
                </div>

                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-start gap-2">
                  <div class="w-7 h-7 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[16px]">wifi_off</mat-icon>
                  </div>
                  <div>
                    <p class="text-[11px] font-black text-slate-900 dark:text-white">100% Offline</p>
                    <p class="text-[9px] text-slate-500 dark:text-zinc-400">Continuous local cache</p>
                  </div>
                </div>

                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-start gap-2">
                  <div class="w-7 h-7 rounded-xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[16px]">open_in_new</mat-icon>
                  </div>
                  <div>
                    <p class="text-[11px] font-black text-slate-900 dark:text-white">Borderless Window</p>
                    <p class="text-[9px] text-slate-500 dark:text-zinc-400">Window controls overlay</p>
                  </div>
                </div>

                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-start gap-2">
                  <div class="w-7 h-7 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[16px]">notifications_active</mat-icon>
                  </div>
                  <div>
                    <p class="text-[11px] font-black text-slate-900 dark:text-white">App Badging</p>
                    <p class="text-[9px] text-slate-500 dark:text-zinc-400">Dock & launcher badges</p>
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- TAB 2: PWA ENGINE & HARDWARE DIAGNOSTICS -->
          @if (activeTab() === 'engine') {
            <div class="relative z-10 w-full space-y-3.5 text-left animate-ios-fade">
              <!-- Storage Quota Gauge -->
              <div class="p-3.5 rounded-2xl bg-black/5 dark:bg-white/[0.04] border border-black/5 dark:border-white/10 space-y-2">
                <div class="flex items-center justify-between text-xs">
                  <span class="font-bold text-slate-700 dark:text-zinc-300 flex items-center gap-1.5">
                    <mat-icon class="text-ios-blue !text-[16px]">storage</mat-icon> Storage Quota
                  </span>
                  <span class="font-mono text-[11px] text-slate-500 dark:text-zinc-400">
                    {{ pwaService.storageInfo().usageMB }} MB / {{ pwaService.storageInfo().quotaMB }} MB
                  </span>
                </div>
                <div class="w-full h-2 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                  <div
                    class="h-full bg-gradient-to-r from-ios-blue to-purple-500 rounded-full transition-all duration-500"
                    [style.width.%]="pwaService.storageInfo().usagePercent || 1"
                  ></div>
                </div>
                <div class="flex items-center justify-between pt-1">
                  <span class="text-[10px] text-slate-500 dark:text-zinc-400">
                    {{ pwaService.storageInfo().usagePercent }}% of allocated device disk space
                  </span>
                  <button
                    type="button"
                    (click)="pwaService.clearOfflineCache()"
                    class="text-[10px] font-bold text-red-500 hover:underline cursor-pointer"
                  >
                    Purge Cache
                  </button>
                </div>
              </div>

              <!-- Advanced Hardware APIs List -->
              <div class="grid grid-cols-2 gap-2 text-xs">
                <!-- Badging API -->
                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center justify-between">
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-ios-blue !text-[16px]">mark_chat_unread</mat-icon>
                    <span class="font-bold text-[11px] text-slate-800 dark:text-zinc-200">Badging</span>
                  </div>
                  @if (pwaService.hasBadging()) {
                    <button
                      type="button"
                      (click)="toggleBadgeDemo()"
                      class="px-2 py-0.5 rounded-full bg-ios-blue/15 text-ios-blue text-[10px] font-black hover:bg-ios-blue hover:text-white transition-all cursor-pointer"
                    >
                      Test
                    </button>
                  } @else {
                    <span class="text-[10px] text-slate-400">N/A</span>
                  }
                </div>

                <!-- Wake Lock API -->
                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center justify-between">
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-amber-500 !text-[16px]">wb_sunny</mat-icon>
                    <span class="font-bold text-[11px] text-slate-800 dark:text-zinc-200">Wake Lock</span>
                  </div>
                  @if (pwaService.hasWakeLock()) {
                    <button
                      type="button"
                      (click)="pwaService.toggleWakeLock()"
                      class="px-2 py-0.5 rounded-full text-[10px] font-black transition-all cursor-pointer"
                      [class.bg-amber-500]="pwaService.isWakeLockActive()"
                      [class.text-white]="pwaService.isWakeLockActive()"
                      [class.bg-black/10]="!pwaService.isWakeLockActive()"
                      [class.dark:bg-white/10]="!pwaService.isWakeLockActive()"
                    >
                      {{ pwaService.isWakeLockActive() ? 'On' : 'Off' }}
                    </button>
                  } @else {
                    <span class="text-[10px] text-slate-400">N/A</span>
                  }
                </div>

                <!-- Web Share API -->
                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center justify-between">
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-emerald-500 !text-[16px]">share</mat-icon>
                    <span class="font-bold text-[11px] text-slate-800 dark:text-zinc-200">Web Share</span>
                  </div>
                  <button
                    type="button"
                    (click)="pwaService.shareApp()"
                    class="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[10px] font-black hover:bg-emerald-500 hover:text-white transition-all cursor-pointer"
                  >
                    Share
                  </button>
                </div>

                <!-- Network Link -->
                <div class="p-2.5 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center justify-between">
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-purple-500 !text-[16px]">wifi</mat-icon>
                    <span class="font-bold text-[11px] text-slate-800 dark:text-zinc-200">Network</span>
                  </div>
                  <span class="font-mono text-[10px] text-purple-600 dark:text-purple-400 font-bold">
                    {{ pwaService.connectionSpeed() }}
                  </span>
                </div>
              </div>
            </div>
          }

          <!-- TAB 3: APP SHORTCUTS -->
          @if (activeTab() === 'shortcuts') {
            <div class="relative z-10 w-full space-y-2.5 text-left animate-ios-fade">
              <p class="text-[11px] text-slate-500 dark:text-zinc-400">
                Long-press the Kicgle app icon on your home screen or right-click the taskbar icon to trigger these instant shortcuts:
              </p>
              
              <div class="grid grid-cols-2 gap-2">
                <div class="p-3 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-ios-blue/15 text-ios-blue flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[18px]">search</mat-icon>
                  </div>
                  <div>
                    <p class="text-xs font-bold text-slate-900 dark:text-white">Search</p>
                    <p class="text-[9px] text-slate-400 font-mono">/search</p>
                  </div>
                </div>

                <div class="p-3 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-purple-500/15 text-purple-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[18px]">forum</mat-icon>
                  </div>
                  <div>
                    <p class="text-xs font-bold text-slate-900 dark:text-white">Messages</p>
                    <p class="text-[9px] text-slate-400 font-mono">/chat</p>
                  </div>
                </div>

                <div class="p-3 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-pink-500/15 text-pink-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[18px]">photo_library</mat-icon>
                  </div>
                  <div>
                    <p class="text-xs font-bold text-slate-900 dark:text-white">Media</p>
                    <p class="text-[9px] text-slate-400 font-mono">/images</p>
                  </div>
                </div>

                <div class="p-3 rounded-2xl bg-black/5 dark:bg-white/[0.03] border border-black/5 dark:border-white/5 flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center shrink-0">
                    <mat-icon class="!text-[18px]">settings</mat-icon>
                  </div>
                  <div>
                    <p class="text-xs font-bold text-slate-900 dark:text-white">Settings</p>
                    <p class="text-[9px] text-slate-400 font-mono">/settings</p>
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- Modal Action Buttons Footer -->
          <div class="relative z-10 w-full flex items-center gap-3 pt-3">
            <!-- Dismiss / Later Button -->
            <button
              type="button"
              (click)="onDismiss()"
              class="flex-1 h-12 rounded-2xl font-extrabold text-xs uppercase tracking-wider bg-black/5 dark:bg-white/10 text-slate-600 dark:text-zinc-300 hover:bg-black/10 dark:hover:bg-white/15 active:scale-95 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <mat-icon class="!text-[18px]">schedule</mat-icon>
              <span>Later</span>
            </button>

            <!-- Main Primary Button -->
            @if (!pwaService.isInstalled()) {
              <button
                type="button"
                (click)="onInstallAction()"
                class="flex-[1.5] h-12 rounded-2xl font-black text-xs uppercase tracking-wider bg-ios-blue text-white shadow-ios shadow-ios-blue/30 hover:bg-ios-blue/90 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <mat-icon class="!text-[18px]">{{ isNativePromptAvailable() ? 'download' : 'touch_app' }}</mat-icon>
                <span>{{ isNativePromptAvailable() ? 'Install App' : 'Add to Home' }}</span>
              </button>
            } @else {
              <button
                type="button"
                (click)="onClose()"
                class="flex-[1.5] h-12 rounded-2xl font-black text-xs uppercase tracking-wider bg-emerald-600 text-white shadow-ios shadow-emerald-600/30 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <mat-icon class="!text-[18px]">check</mat-icon>
                <span>Ready & Active</span>
              </button>
            }
          </div>
        </div>
      </div>
    }
  `,
})
export class PwaInstallPrompt implements OnInit {
  public pwaService = inject(PwaService);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  activeTab = signal<ActiveTab>('install');
  private autoPromptShown = false;
  private badgeTestValue = 0;

  isOpen = computed(() => {
    return this.pwaService.showInstallModal();
  });

  isNativePromptAvailable = computed(() => {
    return this.pwaService.isInstallable() && !!this.pwaService.deferredPrompt();
  });

  platformIcon = computed(() => {
    switch (this.pwaService.detectedOS()) {
      case 'android':
        return 'phone_android';
      case 'ios':
        return 'phone_iphone';
      case 'windows':
        return 'desktop_windows';
      case 'mac':
        return 'laptop_mac';
      case 'linux':
        return 'computer';
      default:
        return 'devices';
    }
  });

  ngOnInit() {
    if (!this.isBrowser) return;

    this.evaluateAutoPrompt();
  }

  evaluateAutoPrompt() {
    // If running in standalone or already installed, don't show auto prompt
    if (this.pwaService.isInstalled()) {
      return;
    }

    // Check if dismissed in last 24h
    const snoozedAt = localStorage.getItem('kicgle_pwa_snoozed_at');
    if (snoozedAt) {
      const diff = Date.now() - parseInt(snoozedAt, 10);
      if (diff < 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Delay prompt slightly so page transitions and splash complete smoothly
    setTimeout(() => {
      if (!this.pwaService.isInstalled() && !this.autoPromptShown) {
        this.autoPromptShown = true;
        this.pwaService.openInstallModal();
      }
    }, 2500);
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onDismiss();
    }
  }

  onClose() {
    this.pwaService.closeInstallModal();
  }

  onDismiss() {
    this.pwaService.closeInstallModal();
    if (this.isBrowser) {
      localStorage.setItem('kicgle_pwa_snoozed_at', Date.now().toString());
    }
  }

  async onInstallAction() {
    if (this.isNativePromptAvailable()) {
      await this.pwaService.installApp();
    } else {
      this.activeTab.set('install');
    }
  }

  toggleBadgeDemo() {
    this.badgeTestValue = this.badgeTestValue === 0 ? 3 : 0;
    if (this.badgeTestValue > 0) {
      this.pwaService.setAppBadge(this.badgeTestValue);
    } else {
      this.pwaService.clearAppBadge();
    }
  }
}
